// Core imports
import { Fragment, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import PropTypes from 'prop-types';

// Component imports
import { Form, Input, Space, Typography, Upload } from 'antd';
import { CopyOutlined, LoadingOutlined } from '@ant-design/icons';
import MediaWidgetModal from '../Modals/MediaWidgetModal';
import MediaPreviewModal from '../Modals/MediaPreviewModal';
import { PlusIcon } from '../Icons/PlusIcon';

// Misc imports
import { IS_MEDIA_MANAGER_ENABLED } from '../../constants/env';
import { t } from 'i18next';
import { PERMISSIONS_GROUP_ADMIN } from '../../constants/main';
import { truncate } from '../../utils/string/truncate';
import { copyTextToClipboard } from '../../utils/function/copyToClipboard';
import useUserPermissions from '../../utils/hooks/useUserPermissions';

const AvatarFormFieldSelection = ({
  value,
  onChange,
  placeholderImage, // this will appear when there is no image
  loading=false,
  checkPermissions=true,
  isPublicToOrg=true,
  fields={
    inputField: { label: '', placeholder: '', required: true, validationMessage: '' },
    uploadField: { label: '', required: true, validationMessage: '' }
  }
}) => {
  const { orgId, tenantId } = useParams();
  const [fileList, setFileList] = useState([]);
  const [isMediaWidgetOpen, setIsMediaWidgetOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState();
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const userPermissions = useUserPermissions(orgId, tenantId);
  const canEdit = checkPermissions ? userPermissions.some(permission => PERMISSIONS_GROUP_ADMIN.includes(permission)) : true;

  useEffect(() => {
    if (value) {
      setFileList([
        {
          uid: '-1',
          name: 'avatar.png',
          status: 'done',
          url: value
        }
      ]);
    } else {
      setFileList([]);
    }
  }, [value]);

  const triggerChange = (changedValue) => {
    if (typeof onChange === 'function') {
      onChange(changedValue);
    }
    closeMediaWidgetModal();
    if (isPreviewModalOpen) {
      setIsPreviewModalOpen(false);
    }
  };

  const handlePreview = (file) => {
    setPreviewImage(file);
    setIsPreviewModalOpen(true);
  };

  const openMediaWidgetModal = () => {
    setIsMediaWidgetOpen(true);
  };

  const closeMediaWidgetModal = (file) => {
    if(file?.url) {
      triggerChange(file?.url);
    }

    const currentTab = searchParams.get('mediaTab');
    if (currentTab === 'library' || currentTab === 'upload') {
      searchParams.delete('mediaTab');
      setSearchParams(searchParams);
    }

    setIsMediaWidgetOpen(false);
  };

  const renderUploadButton = () => {
    return (
      <div
        onClick={() => {
          if (canEdit) {
            openMediaWidgetModal();
          }
        }}
      >
        {loading ? <LoadingOutlined /> : <PlusIcon />}
        <div className="mt-2">
          {t('Select New')}
        </div>
      </div>
    );
  };

  return (
    <Fragment>
      {/* Hidden input field to store the image URL, we only need that */}
      <Input
        type="hidden"
        value={value}
        onChange={(e) => {
          triggerChange(e.target.value);
        }}
      />

      {
        fields.uploadField && IS_MEDIA_MANAGER_ENABLED && (
          <Fragment>
            <Space>
              {
                !value && placeholderImage ? (
                  <Fragment>
                    {placeholderImage}
                  </Fragment>
                ) : null
              }

              <Form.Item
                label={fields?.uploadField?.label}
                rules={[{
                  required: fields?.uploadField?.required,
                  message: fields?.uploadField?.validationMessage
                }]}
                noStyle
              >
                <Upload
                  name="avatar"
                  listType="picture-card"
                  className={classNames('avatar-uploader', {
                    'animation-duration-0': placeholderImage
                  })}
                  fileList={fileList}
                  openFileDialogOnClick={false}
                  onRemove={(_file) => {
                    triggerChange('');
                  }}
                  onPreview={(file) => handlePreview(file)}
                  //Override download action to use as copy!
                  onDownload={(file) => copyTextToClipboard(file.url)}
                  showUploadList={{ showRemoveIcon: true, showPreviewIcon: true, showDownloadIcon: true, downloadIcon: <CopyOutlined style={{ color: 'white' }}/> }}
                  disabled={!canEdit || loading}
                >
                  {renderUploadButton()}
                </Upload>
              </Form.Item>
            </Space>

            {
              canEdit ? (
                <MediaWidgetModal
                  title={t('Add Image')}
                  onSuccess={(url) => triggerChange(url)}
                  onClose={closeMediaWidgetModal}
                  isModalOpen={isMediaWidgetOpen}
                  isPublicToOrg={isPublicToOrg}
                />
              ) : null
            }
            {isPreviewModalOpen ? (
              <MediaPreviewModal
                id={previewImage.uid}
                file={previewImage}
                onPreviewClose={() => setIsPreviewModalOpen(false)}
                handleDelete={() => triggerChange('')}
                isPreviewModalOpen={isPreviewModalOpen}
              />
            ) : null}
          </Fragment>
        )
      }

      {
        fields.inputField && (
          <Fragment>
            <Form.Item
              label={`${IS_MEDIA_MANAGER_ENABLED ? t('or') : ''} ${fields?.inputField?.label}`}
              rules={[{
                required: fields?.inputField?.required,
                message: fields?.inputField?.validationMessage
              }]}
            >
              {
                canEdit ? (
                  <Input
                    value={value}
                    size="large"
                    placeholder={fields?.inputField?.placeholder}
                    onChange={(event) => {
                      triggerChange(event.target.value);
                    }}
                  />
                ) : (
                  <Fragment>
                    <Typography.Text className="mb-0" copyable={value ? { text: value } : false}>
                      {value ? truncate(value, 80, '...') : t('(No image available)')}
                    </Typography.Text>
                  </Fragment>
                )
              }
            </Form.Item>
          </Fragment>
        )
      }
    </Fragment>
  );
};

AvatarFormFieldSelection.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholderImage: PropTypes.element,
  loading: PropTypes.bool,
  fields: PropTypes.object,
  shouldValidate: PropTypes.bool,
  checkPermissions: PropTypes.bool,
  isPublicToOrg: PropTypes.bool
};

export default AvatarFormFieldSelection;
